/**
 * FERP PDF Shape Overlay (DRR-079)
 *
 * SVG overlay for drawing rectangles, circles, arrows, and lines.
 * Renders existing shape annotations + captures new shapes via drag.
 */

import { useState, useCallback, useRef, type MouseEvent } from 'react';
import { Box } from '@mantine/core';
import { useAuth } from '@/auth';
import { usePdfSuite } from './PdfSuiteContext';
import type { PdfAnnotation, PdfPoint } from './types';

interface ShapeOverlayProps {
  page: number;
  scale: number;
  annotations: PdfAnnotation[];
}

export function ShapeOverlay({ page, scale, annotations }: ShapeOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [dragStart, setDragStart] = useState<PdfPoint | null>(null);
  const [dragEnd, setDragEnd] = useState<PdfPoint | null>(null);
  const { state, addAnnotation } = usePdfSuite();
  const { user } = useAuth();

  const getRelative = useCallback((e: MouseEvent): PdfPoint | null => {
    const el = overlayRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  }, []);

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (state.viewer.annotationMode !== 'shape') return;
      const pt = getRelative(e);
      if (pt) {
        setDragStart(pt);
        setDragEnd(pt);
      }
    },
    [state.viewer.annotationMode, getRelative],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragStart) return;
      const pt = getRelative(e);
      if (pt) setDragEnd(pt);
    },
    [dragStart, getRelative],
  );

  const handleMouseUp = useCallback(() => {
    if (!dragStart || !dragEnd) {
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    const minX = Math.min(dragStart.x, dragEnd.x);
    const minY = Math.min(dragStart.y, dragEnd.y);
    const w = Math.abs(dragEnd.x - dragStart.x);
    const h = Math.abs(dragEnd.y - dragStart.y);

    if (w > 0.01 || h > 0.01) {
      addAnnotation({
        id: `pending_${Date.now()}`,
        type: 'shape',
        page,
        rect: { x: minX, y: minY, width: w, height: h },
        content: '',
        color: state.viewer.inkColor,
        strokeWidth: state.viewer.inkStrokeWidth,
        shapeType: state.viewer.selectedShape,
        startPoint: dragStart,
        endPoint: dragEnd,
        createdBy: user?.id ?? 'unknown',
        createdAt: new Date().toISOString(),
      });
    }

    setDragStart(null);
    setDragEnd(null);
  }, [dragStart, dragEnd, page, state.viewer, addAnnotation, user?.id]);

  const shapeAnnotations = annotations.filter((a) => a.type === 'shape');

  const renderShape = (ann: PdfAnnotation, isPreview = false) => {
    const shape = ann.shapeType ?? 'rectangle';
    const color = ann.color ?? '#000000';
    const sw = ann.strokeWidth ?? 2;
    const opacity = isPreview ? 0.5 : 1;
    const r = ann.rect;

    if (shape === 'rectangle') {
      return (
        <rect
          x={`${r.x * 100}%`}
          y={`${r.y * 100}%`}
          width={`${r.width * 100}%`}
          height={`${r.height * 100}%`}
          fill="none"
          stroke={color}
          strokeWidth={sw}
          opacity={opacity}
        />
      );
    }
    if (shape === 'circle') {
      const cx = (r.x + r.width / 2) * 100;
      const cy = (r.y + r.height / 2) * 100;
      const rx = (r.width / 2) * 100;
      const ry = (r.height / 2) * 100;
      return (
        <ellipse
          cx={`${cx}%`}
          cy={`${cy}%`}
          rx={`${rx}%`}
          ry={`${ry}%`}
          fill="none"
          stroke={color}
          strokeWidth={sw}
          opacity={opacity}
        />
      );
    }
    if (shape === 'arrow' || shape === 'line') {
      const start = ann.startPoint ?? { x: r.x, y: r.y };
      const end = ann.endPoint ?? { x: r.x + r.width, y: r.y + r.height };
      return (
        <line
          x1={`${start.x * 100}%`}
          y1={`${start.y * 100}%`}
          x2={`${end.x * 100}%`}
          y2={`${end.y * 100}%`}
          stroke={color}
          strokeWidth={sw}
          opacity={opacity}
          markerEnd={shape === 'arrow' ? 'url(#arrowhead)' : undefined}
        />
      );
    }
    return null;
  };

  // Build preview annotation if dragging
  const preview: PdfAnnotation | null =
    dragStart && dragEnd
      ? {
          id: 'preview',
          type: 'shape',
          page,
          rect: {
            x: Math.min(dragStart.x, dragEnd.x),
            y: Math.min(dragStart.y, dragEnd.y),
            width: Math.abs(dragEnd.x - dragStart.x),
            height: Math.abs(dragEnd.y - dragStart.y),
          },
          content: '',
          color: state.viewer.inkColor,
          strokeWidth: state.viewer.inkStrokeWidth,
          shapeType: state.viewer.selectedShape,
          startPoint: dragStart,
          endPoint: dragEnd,
          createdBy: '',
          createdAt: '',
        }
      : null;

  return (
    <Box
      ref={overlayRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: state.viewer.annotationMode === 'shape' ? 'auto' : 'none',
        cursor: state.viewer.annotationMode === 'shape' ? 'crosshair' : 'default',
        zIndex: 12,
      }}
    >
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="10"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="currentColor" />
          </marker>
        </defs>
        {shapeAnnotations.map((ann) => (
          <g key={ann.id}>{renderShape(ann)}</g>
        ))}
        {preview && <g>{renderShape(preview, true)}</g>}
      </svg>
    </Box>
  );
}
